import { isAxiosError } from "axios";
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import isString from "lodash/isString";
import uniq from "lodash/uniq";
import type { MenuProduct } from "@/api/vendure/menu/menu.types";
import type { DeliveryMethod } from "@/api/vendure/merchant/merchant.types";
import { DELIVERY_METHOD_TYPE } from "@/api/vendure/merchant/merchant.types";
import type {
  FlushCartContext,
  FlushCartItem,
  FlushDeliveryAddress,
} from "@/api/vendure/order/order.types";
import {
  IngredientRemovalRefusal,
  ORDER_SOURCE,
} from "@/api/vendure/order/order.types";
import type { CartItem } from "@/stores/cart.store";
import type { DeliveryAddress } from "@/stores/delivery.store";

function removedIngredientIdsOf(item: CartItem): number[] {
  return uniq(
    (item.removedIngredients ?? []).map((ingredient) => ingredient.id),
  );
}

export function buildFlushItems(items: CartItem[]): FlushCartItem[] {
  return items.map((item) => ({
    productId: item.productId,
    quantity: item.quantity,
    ...(item.comment ? { comment: item.comment } : {}),
    selections: Object.values(item.options)
      .map((opt) => ({
        optionId: opt.id,
        items: Object.values(opt.suboptions)
          .filter((sub) => sub.selected && sub.quantity > 0)
          .map((sub) => ({ subOptionId: sub.id, quantity: sub.quantity })),
      }))
      .filter((sel) => sel.items.length > 0),
    ...(isEmpty(item.removedIngredients)
      ? {}
      : { removedIngredientIds: removedIngredientIdsOf(item) }),
  }));
}

const REFUSALS_THAT_NEED_A_FRESH_MENU: readonly string[] = Object.values(
  IngredientRemovalRefusal,
);

export function isIngredientRemovalRefusal(error: unknown): boolean {
  const errorCode: unknown = isAxiosError(error)
    ? get(error.response?.data, "errorCode")
    : undefined;
  return (
    isString(errorCode) && REFUSALS_THAT_NEED_A_FRESH_MENU.includes(errorCode)
  );
}

export function linesWithRemovalsNoLongerOffered(
  items: CartItem[],
  productsById: Record<string, MenuProduct>,
): CartItem[] {
  return items.filter((item) => {
    const offeredIds = (
      productsById[String(item.productId)]?.ingredient_section?.ingredients ??
      []
    ).map((ingredient) => ingredient.id);
    return removedIngredientIdsOf(item).some((id) => !offeredIds.includes(id));
  });
}

export function buildScheduledFor(
  day: number | null,
  time: string | null,
): string | null {
  if (!time) return null;
  const date = day !== null ? new Date(day) : new Date();
  const [h, m] = time.split(":").map(Number);
  date.setHours(h, m, 0, 0);
  return date.toISOString();
}

export function buildCartContext(params: {
  deliveryMethod: DeliveryMethod;
  selectedDay: number | null;
  selectedTime: string | null;
  deliveryInstructions: string;
  tableSpot: string | null;
  tableSpotType?: string;
}): FlushCartContext {
  const {
    deliveryMethod,
    selectedDay,
    selectedTime,
    deliveryInstructions,
    tableSpot,
    tableSpotType = "table",
  } = params;
  const isDelivery = deliveryMethod === "delivery";
  const isDineIn = deliveryMethod === "dine_in";

  return {
    type: DELIVERY_METHOD_TYPE[deliveryMethod],
    scheduledFor: buildScheduledFor(selectedDay, selectedTime),
    specialInstructions: isDelivery ? deliveryInstructions || null : null,
    supportType: isDineIn && tableSpot ? tableSpotType : null,
    supportNumber: isDineIn ? tableSpot : null,
    orderSource: ORDER_SOURCE,
  };
}

export function buildDeliveryAddress(
  addr: DeliveryAddress,
): FlushDeliveryAddress {
  const hasCoords = addr.lat !== 0 && addr.lon !== 0;
  return {
    streetLine1: addr.streetLine1 || addr.address,
    streetLine2: addr.streetLine2 || null,
    city: addr.city || null,
    postalCode: addr.postalCode || null,
    countryCode: addr.countryCode || null,
    fullName: addr.fullName || null,
    locationType: addr.locationType ?? null,
    entrance: addr.entrance || null,
    floor: addr.floor || null,
    instructions: addr.instructions || null,
    ...(hasCoords ? { latitude: addr.lat, longitude: addr.lon } : {}),
  };
}
